import { FRAME_TYPE_ICONS } from "@/components/ui/concept-icons";
import { useEffect, useRef, useState, type FocusEvent } from "react";
import { Button, Surface } from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import { useT } from "@/i18n";
import { canvasMessages } from "@/i18n/messages/canvas";
import { cn } from "@/lib/cn";

const OPEN_DELAY_MS = 140;
const CLOSE_DELAY_MS = 280;

interface FrameToolsLauncherProps {
  onNote?: () => void;
  noteActive?: boolean;
  onTerminal?: () => void;
  onTaskboard?: () => void;
  onMemory?: () => void;
}

interface FrameToolAction {
  id: string;
  label: string;
  testId: string;
  onClick?: () => void;
  active?: boolean;
  icon: typeof Icons.StickyNote;
}

export function FrameToolsLauncher({
  onNote,
  noteActive = false,
  onTerminal,
  onTaskboard,
  onMemory,
}: FrameToolsLauncherProps) {
  const t = useT(canvasMessages);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const disabled = !onNote && !onTerminal && !onTaskboard && !onMemory;

  const actions: FrameToolAction[] = [
    {
      id: "note",
      label: t("addNote"),
      testId: "toolbar-new-frame",
      onClick: onNote,
      active: noteActive,
      icon: FRAME_TYPE_ICONS.note,
    },
    {
      id: "terminal",
      label: t("toolTerminal"),
      testId: "toolbar-new-terminal",
      onClick: onTerminal,
      icon: FRAME_TYPE_ICONS.terminal,
    },
    {
      id: "taskboard",
      label: t("toolTaskBoard"),
      testId: "toolbar-new-taskboard",
      onClick: onTaskboard,
      icon: FRAME_TYPE_ICONS.taskboard,
    },
    {
      id: "memory",
      label: t("toolMemory"),
      testId: "toolbar-new-memory",
      onClick: onMemory,
      icon: FRAME_TYPE_ICONS.memory,
    },
  ];

  function clearTimers() {
    if (openTimerRef.current) clearTimeout(openTimerRef.current);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    openTimerRef.current = null;
    closeTimerRef.current = null;
  }

  function scheduleOpen() {
    if (disabled || open) return;
    clearTimers();
    openTimerRef.current = setTimeout(() => {
      openTimerRef.current = null;
      setOpen(true);
    }, OPEN_DELAY_MS);
  }

  function scheduleClose() {
    if (pinned) return;
    clearTimers();
    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null;
      setOpen(false);
    }, CLOSE_DELAY_MS);
  }

  function close() {
    clearTimers();
    setPinned(false);
    setOpen(false);
  }

  function handleBlur(event: FocusEvent<HTMLDivElement>) {
    if (
      event.relatedTarget instanceof Node &&
      rootRef.current?.contains(event.relatedTarget)
    ) {
      return;
    }
    scheduleClose();
  }

  useEffect(
    () => () => {
      if (openTimerRef.current) clearTimeout(openTimerRef.current);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    },
    [],
  );

  return (
    <div
      ref={rootRef}
      className="relative"
      onPointerEnter={scheduleOpen}
      onPointerLeave={scheduleClose}
      onFocusCapture={() => {
        clearTimers();
        if (!disabled) setOpen(true);
      }}
      onBlurCapture={handleBlur}
      onKeyDown={(event) => {
        if (event.key !== "Escape") return;
        event.preventDefault();
        close();
        triggerRef.current?.focus();
      }}
      data-testid="rail-frame-tools"
    >
      <Button
        ref={triggerRef}
        type="button"
        variant="ghost"
        size="icon"
        disabled={disabled}
        className={cn(
          "h-9 w-9 rounded-full p-0 text-muted hover:text-primary",
          (open || noteActive) && "text-accent hover:text-accent",
        )}
        aria-label={t("frameTools")}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls="rail-frame-tools-menu"
        data-testid="rail-frame-tools-trigger"
        onClick={() => {
          if (disabled) return;
          clearTimers();
          setPinned((current) => !current);
          setOpen((current) => !current || !pinned);
        }}
      >
        <Icons.Plus className="h-4 w-4" />
      </Button>

      {open ? (
        <div
          className="absolute bottom-0 left-full z-50 pl-2"
          data-testid="rail-frame-tools-hover-bridge"
        >
          <Surface
            id="rail-frame-tools-menu"
            surfaceRole="rail"
            nativeLevel="island"
            nativeGroup="frame-tools"
            role="menu"
            aria-label={t("createFrame")}
            className="flex items-center gap-1 p-1"
            data-testid="rail-frame-tools-menu"
          >
            {actions.map((action) => {
              const Icon = action.icon;
              return (
                <Button
                  key={action.id}
                  type="button"
                  variant="ghost"
                  size="icon"
                  role="menuitem"
                  disabled={!action.onClick}
                  className={cn(
                    "group relative h-9 w-9 rounded-full p-0 text-muted hover:text-primary",
                    action.active && "text-accent hover:text-accent",
                  )}
                  aria-label={action.label}
                  data-testid={action.testId}
                  onClick={() => {
                    action.onClick?.();
                    close();
                  }}
                >
                  <Icon className="h-4 w-4" />
                  <span className="denker-glass-tooltip pointer-events-none absolute -top-8 left-1/2 z-50 -translate-x-1/2 whitespace-nowrap rounded-full border px-2.5 py-1 text-canvas-md font-medium opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                    {action.label}
                  </span>
                </Button>
              );
            })}
          </Surface>
        </div>
      ) : null}
    </div>
  );
}

export type { FrameToolsLauncherProps };
